import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard,
  Calendar,
  CalendarDays,
  Package,
  Image,
  MessageSquare,
  Settings,
  LogOut,
  Menu,
  X,
  Tag,
  Users,
  Search
} from 'lucide-react';
import './AdminLayout.css';

// Role-based navigation permissions
const ROLE_PERMISSIONS = {
  super_admin: ['dashboard', 'bookings', 'calendar', 'packages', 'promo-codes', 'gallery', 'testimonials', 'users', 'seo', 'settings'],
  admin: ['dashboard', 'bookings', 'calendar', 'packages', 'promo-codes', 'gallery', 'testimonials', 'seo'],
  content_manager: ['dashboard', 'gallery', 'testimonials']
};

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const allNavItems = [
    { path: '/admin/dashboard', key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/bookings', key: 'bookings', label: 'Bookings', icon: Calendar },
    { path: '/admin/calendar', key: 'calendar', label: 'Calendar', icon: CalendarDays },
    { path: '/admin/packages', key: 'packages', label: 'Packages', icon: Package },
    { path: '/admin/promo-codes', key: 'promo-codes', label: 'Promo Codes', icon: Tag },
    { path: '/admin/gallery', key: 'gallery', label: 'Gallery', icon: Image },
    { path: '/admin/testimonials', key: 'testimonials', label: 'Testimonials', icon: MessageSquare },
    { path: '/admin/users', key: 'users', label: 'Users', icon: Users },
    { path: '/admin/seo', key: 'seo', label: 'SEO', icon: Search },
    { path: '/admin/settings', key: 'settings', label: 'Settings', icon: Settings }
  ];

  const userRole = user?.role || 'content_manager';
  const allowedKeys = ROLE_PERMISSIONS[userRole] || ROLE_PERMISSIONS.content_manager;
  const navItems = allNavItems.filter(item => allowedKeys.includes(item.key));

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'admin': return 'Admin';
      case 'content_manager': return 'Content Manager';
      default: return role;
    }
  };

  return (
    <div className="admin-layout">
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2>Tropiluxe</h2>
          <p className="sidebar-subtitle">Admin Panel</p>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${isActive ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.key}`}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              {user?.full_name?.charAt(0) || 'A'}
            </div>
            <div className="user-details">
              <div className="user-name">{user?.full_name}</div>
              <div className="user-role">{getRoleLabel(user?.role)}</div>
            </div>
          </div>
          <button onClick={handleLogout} className="logout-btn" data-testid="admin-logout-btn">
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <div className="admin-main">
        <header className="admin-header">
          <button
            className="mobile-menu-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <div className="header-title">
            {navItems.find(item => item.path === location.pathname)?.label || 'Admin'}
          </div>
        </header>

        <main className="admin-content">
          <Outlet />
        </main>
      </div>

      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
